import { Link } from '@tanstack/react-router'
import { AlertTriangle, ArrowUpRight, Info } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { metricPairOrEmpty } from '@/components/admin/dashboard/dashboardFormat'
import type { OverviewReport } from '@/types'
import { TermLabel, useMoney } from './moneyUi'
import { hasNetSales, shareSentence } from './pnlStatementLogic'

type Props = {
  projected: OverviewReport
  /** Only a role that can open Manage Menu is offered the way in. */
  canEditCosting: boolean
}

/** Below this share of sales costed, the cost of goods is more guess than figure. */
const LOW_COVERAGE = 50

/**
 * How far to trust the cost of goods (F-FE-N164). The statement's COGS comes
 * from the cost set on each menu item, so it is only as complete as the menu
 * costing — this card says how complete, and shows the margin that costing
 * predicts so it can be held against the statement. One band under the
 * statement, because it is a footnote to it, not a second statement.
 *
 * It replaces a strip that printed the same three figures as a sentence with
 * chevrons between them, in three colours, above the ledger's ladder.
 */
export function CostingCheckCard({ projected, canEditCosting }: Props) {
  const money = useMoney()
  const netSales = projected.net_sales.current
  const cogs = metricPairOrEmpty(projected.projected_cogs).current
  const margin = metricPairOrEmpty(projected.projected_margin).current
  const coverage = Math.max(0, Math.min(100, metricPairOrEmpty(projected.projected_coverage_pct).current))
  // With no sales there is nothing to cost, so the coverage warning would be
  // a false alarm rather than a finding.
  const sold = hasNetSales(netSales)
  const low = sold && coverage < LOW_COVERAGE

  return (
    // Laid out by the card's own width: beside an open sidebar the page is
    // 256px narrower than the viewport says.
    <Card className="@container">
      <CardContent className="grid gap-x-8 gap-y-5 p-6 @4xl:grid-cols-[minmax(0,5fr)_minmax(0,4fr)_minmax(0,7fr)] @4xl:items-start">
        <div>
          <h3 className="flex items-center gap-1.5 text-base font-semibold leading-none tracking-tight">
            Menu costing check
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label="How this differs from the statement"
                >
                  <Info className="h-4 w-4" aria-hidden />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm space-y-2 text-left">
                <p>
                  The projected margin is net sales less quantity sold times the cost set on each item. Items with no
                  cost set are left out.
                </p>
                <p>
                  It counts sales by the day the order was placed; the statement counts them by the day it was
                  completed, so the two can differ by a few orders at the edges of the period.
                </p>
                <p>
                  If ingredient purchases are also recorded as expenses, both figures already carry a form of cost.
                  Read them as two estimates; do not add them together.
                </p>
              </TooltipContent>
            </Tooltip>
          </h3>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            The statement&rsquo;s cost of goods comes from the cost set on each menu item, so it is only as complete as
            the costing.
          </p>
          {canEditCosting && (
            <Link
              to="/admin/menu"
              search={{ tab: 'costing' }}
              className="mt-3 inline-flex items-center gap-1 rounded-sm text-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Set item costs
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          )}
        </div>

        <div>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <TermLabel label="Sales with a cost set" metric="cost_coverage_pct" className="text-muted-foreground" />
            <span className="font-medium tabular-nums">{coverage.toFixed(0)}%</span>
          </div>
          <div
            className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"
            role="meter"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(coverage)}
            aria-label="Share of sales with a cost set"
          >
            <div
              className={low ? 'h-full rounded-full bg-warning-solid' : 'h-full rounded-full bg-foreground/70'}
              style={{ width: `${coverage}%` }}
            />
          </div>
          <p className={low ? 'mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-warning-fg' : 'mt-2 text-xs leading-relaxed text-muted-foreground'}>
            {low && <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />}
            {!sold
              ? 'Nothing was sold in this period, so there is nothing to cost.'
              : low
                ? 'Under half of sales are costed, so cost of goods is understated and profit overstated.'
                : coverage >= 99.5
                  ? 'Every item sold had a cost set.'
                  : `The other ${(100 - coverage).toFixed(0)}% was sold with no cost set, so it counts as pure margin.`}
          </p>
        </div>

        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm @md:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">Net sales</dt>
            <dd className="mt-1 font-medium tabular-nums">{money.whole(netSales)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Projected cost of goods</dt>
            <dd className="mt-1 font-medium tabular-nums">{money.whole(cogs)}</dd>
          </div>
          <div>
            <dt>
              <TermLabel label="Projected margin" metric="projected_margin" className="text-muted-foreground" />
            </dt>
            <dd className={margin < 0 ? 'mt-1 font-medium tabular-nums text-danger-fg' : 'mt-1 font-medium tabular-nums'}>
              {money.whole(margin)}
              {sold && (
                <span className="block text-xs font-normal text-muted-foreground">
                  {shareSentence(margin, netSales, 'of net sales', '')}
                </span>
              )}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  )
}
